import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/components/AuthProvider';
import { ACCOUNT_UPDATES_URL } from '@/components/layout/SidebarAccountFooter';
import {
  getLatestRelease,
  markReleaseSeenPersistent,
  shouldShowWhatsNewModal,
  formatReleaseDate,
  formatVersion,
  highlightKindLabel,
  getWhatsNewSeenIdFromPreferences,
  WHATS_NEW_SEEN_EVENT,
} from '@/lib/releaseNotes';
import {
  hasBlockingDialog,
  subscribeBlockingDialogs,
} from '@/lib/blockingDialogs';
import { cn } from '@/lib/utils';

function resolveUserId(user: { id?: string } | null | undefined): string | null {
  const raw = user?.id;
  if (raw == null || raw === '') return null;
  return String(raw).trim() || null;
}

export function WhatsNewModal() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = resolveUserId(user);
  const [open, setOpen] = useState(false);
  const latest = getLatestRelease();
  const lockedOutRef = useRef(false);
  const ignoreSeenEventRef = useRef(false);

  const prefsSeenId = getWhatsNewSeenIdFromPreferences(user?.preferences);

  useEffect(() => {
    if (!latest || !userId) return;

    lockedOutRef.current = !shouldShowWhatsNewModal(userId, undefined, user?.preferences);

    let cancelled = false;
    let openTimer: number | undefined;

    const markAndLock = () => {
      ignoreSeenEventRef.current = true;
      lockedOutRef.current = true;
      void markReleaseSeenPersistent(latest.id, userId).finally(() => {
        ignoreSeenEventRef.current = false;
      });
    };

    const tryOpen = () => {
      if (cancelled || lockedOutRef.current) return;
      if (hasBlockingDialog()) return;
      if (!shouldShowWhatsNewModal(userId, undefined, user?.preferences)) {
        lockedOutRef.current = true;
        return;
      }
      markAndLock();
      setOpen(true);
    };

    const onSeenElsewhere = () => {
      if (ignoreSeenEventRef.current) return;
      lockedOutRef.current = true;
      setOpen(false);
    };

    if (!lockedOutRef.current) {
      openTimer = window.setTimeout(tryOpen, 800);
    }

    const unsubBlockers = subscribeBlockingDialogs(() => {
      if (cancelled || lockedOutRef.current) return;
      if (hasBlockingDialog()) {
        setOpen(false);
        return;
      }
      window.clearTimeout(openTimer);
      openTimer = window.setTimeout(tryOpen, 400);
    });

    window.addEventListener(WHATS_NEW_SEEN_EVENT, onSeenElsewhere);

    return () => {
      cancelled = true;
      window.clearTimeout(openTimer);
      unsubBlockers();
      window.removeEventListener(WHATS_NEW_SEEN_EVENT, onSeenElsewhere);
    };
  }, [latest?.id, userId, prefsSeenId]);

  if (!latest || !userId) return null;

  const close = () => {
    void markReleaseSeenPersistent(latest.id, userId);
    lockedOutRef.current = true;
    setOpen(false);
  };

  const viewAll = () => {
    close();
    navigate(ACCOUNT_UPDATES_URL);
  };

  const tryFeature = (href: string) => {
    close();
    navigate(href);
  };

  const firstTryHref = latest.highlights.find((h) => h.href)?.href;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent
        className="sm:max-w-md gap-0 p-0 overflow-hidden"
        data-testid="whats-new-modal"
      >
        <div className="bg-brand px-6 py-5 text-white">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <div className="inline-flex items-center gap-1.5 text-[11px] font-medium tracking-wide text-white/90">
              <Sparkles className="w-3.5 h-3.5" />
              WHAT&apos;S NEW
            </div>
            <span
              className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-[11px] font-mono font-semibold text-brand"
              data-testid="whats-new-modal-version"
            >
              {formatVersion(latest.version)}
            </span>
          </div>
          <DialogHeader className="space-y-1.5 text-left">
            <DialogTitle className="text-lg text-white tracking-tight">
              {latest.title}
            </DialogTitle>
            <DialogDescription className="text-white/80 text-sm">
              {latest.summary}
            </DialogDescription>
          </DialogHeader>
          <p className="text-[11px] text-white/60 mt-3">
            Shipped {formatReleaseDate(latest.date)}
            {latest.label ? ` · ${latest.label}` : ''}
          </p>
        </div>

        <ul className="max-h-[40vh] overflow-y-auto divide-y divide-gray-50 px-1">
          {latest.highlights.slice(0, 4).map((h) => (
            <li key={h.title} className="flex items-start gap-3 px-5 py-3">
              <div className="mt-0.5 p-1.5 rounded-md bg-brand/10 shrink-0">
                <Sparkles className="w-3 h-3 text-brand" />
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-medium text-[#08090A]">{h.title}</p>
                  <span
                    className={cn(
                      'inline-flex items-center rounded px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wide',
                      (h.kind ?? 'new') === 'improved'
                        ? 'bg-sky-50 text-sky-700'
                        : (h.kind ?? 'new') === 'fix'
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-brand/10 text-brand',
                    )}
                  >
                    {highlightKindLabel(h.kind)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {h.description}
                </p>
              </div>
              {h.href && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-brand hover:text-[#159a5a] hover:bg-brand/10 h-8 px-2"
                  onClick={() => tryFeature(h.href!)}
                >
                  Try it
                  <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              )}
            </li>
          ))}
        </ul>

        <DialogFooter className="flex-row flex-wrap justify-end gap-2 px-5 py-4 border-t border-gray-100 sm:space-x-0">
          <Button variant="ghost" size="sm" onClick={close}>
            Got it
          </Button>
          <Button variant="outline" size="sm" onClick={viewAll}>
            View all
          </Button>
          {firstTryHref && (
            <Button
              size="sm"
              className="bg-brand hover:bg-[#159a5a] text-white"
              onClick={() => tryFeature(firstTryHref)}
            >
              Try it
              <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
